import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AutomationError } from "~/api/errors";
import type { JumpMode } from "~/api/opsTypes";
import { useJumpRun } from "~/api/queries";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";

/**
 * Each mode in the words that describe what happens to the work already done — "rewind" alone
 * does not say whether the later steps are thrown away (they are not; they are kept, marked
 * superseded), and that is the fact an operator needs before pressing it.
 */
const MODES: Array<{ value: JumpMode; label: string; explanation: string }> = [
  {
    value: "rewind",
    label: "Rewind",
    explanation: "Move back and re-run from this node; later steps are kept as superseded.",
  },
  {
    value: "skip-to",
    label: "Skip to",
    explanation: "Move forward; the skipped nodes are marked with your note.",
  },
  {
    value: "retry-node",
    label: "Retry node",
    explanation: "Re-run the current node, keeping its previous attempt as superseded.",
  },
];

export interface JumpDialogProps {
  runId: string;
  open: boolean;
  onClose: () => void;
  currentNodeId: string | null;
  /**
   * Only nodes the run recorded a step for. The server refuses anything else with
   * `unknown_node`, so offering the whole graph would be offering refusals.
   */
  nodes: Array<{ id: string; name: string }>;
}

export function JumpDialog({ runId, open, onClose, currentNodeId, nodes }: JumpDialogProps) {
  const { t } = useTranslation();
  const jump = useJumpRun();
  const [mode, setMode] = useState<JumpMode>("rewind");
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<{ message: string; knownNodes?: string[] } | null>(null);

  // `retry-node` is about the cursor's own node, so preselect it rather than making the reader
  // find it in a list they are not really choosing from.
  useEffect(() => {
    if (mode === "retry-node" && currentNodeId) {
      setTarget(currentNodeId);
    }
  }, [mode, currentNodeId]);

  useEffect(() => {
    if (open) {
      setRefusal(null);
    }
  }, [open]);

  // A refusal that names the nodes it WOULD have accepted is the fix, not just the complaint.
  const offered =
    refusal?.knownNodes && refusal.knownNodes.length > 0
      ? refusal.knownNodes.map((id) => ({ id, name: id }))
      : nodes;

  const submit = () => {
    if (!target) {
      setRefusal({ message: t("Pick the node to move the cursor to.") });
      return;
    }
    setRefusal(null);
    jump.mutate(
      {
        id: runId,
        target: { toNodeId: target, mode, ...(note.trim() ? { note: note.trim() } : {}) },
      },
      {
        onSuccess: () => {
          toast.success(t("Run cursor moved to “{{node}}”.", { node: target }));
          onClose();
        },
        onError: (error: unknown) => {
          if (error instanceof AutomationError) {
            setRefusal({
              message: error.message,
              ...(error.knownNodes ? { knownNodes: error.knownNodes } : {}),
            });
            return;
          }
          setRefusal({ message: error instanceof Error ? error.message : t("The jump failed.") });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t("Move the run cursor")}
      closeLabel={t("Close")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} disabled={jump.isPending}>
            {t("Move cursor")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <fieldset className="flex flex-col gap-2">
          <legend className="text-small font-medium text-text-secondary">{t("What to do")}</legend>
          {MODES.map((entry) => (
            <label key={entry.value} className="flex items-start gap-2">
              <input
                type="radio"
                name="jump-mode"
                value={entry.value}
                checked={mode === entry.value}
                onChange={() => setMode(entry.value)}
                className="mt-1"
              />
              <span className="flex flex-col">
                <span className="text-body text-text">{t(entry.label)}</span>
                <span className="text-small text-text-tertiary">{t(entry.explanation)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Select
          label={t("Target node")}
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          hint={t(
            "Only nodes this run recorded a step for — the server refuses anything else, so they are not offered.",
          )}
        >
          <option value="">{t("Choose a node…")}</option>
          {offered.map((node) => (
            <option key={node.id} value={node.id}>
              {node.name}
            </option>
          ))}
        </Select>

        <Input
          label={t("Note")}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t("Why the cursor moved — recorded on the run")}
        />

        {refusal ? (
          <p role="alert" className="text-small text-danger">
            {refusal.message}
            {refusal.knownNodes && refusal.knownNodes.length > 0 ? (
              <span className="block text-text-secondary">
                {t("The server accepts these nodes: {{nodes}}", {
                  nodes: refusal.knownNodes.join(", "),
                })}
              </span>
            ) : null}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
